// The one place the `users/{uid}` profile document is seeded.
//
// WHY THIS MODULE EXISTS. The write used to live inline in App.js's
// `signInWithGoogle`, which was the only way into the app — so "sign in" and
// "seed the profile" were the same code path by construction. Phase 50 added a
// second entrance: `/signup` is public, and its CTA signs the visitor in
// directly with `signInWithPopup` before calling the signup callables. That
// path bypassed App.js entirely, so every self-serve signup landed with no
// `users/{uid}` document at all.
//
// The damage is quiet, which is what makes it worth a module rather than a
// second copy of the write. `admin/components/views/UsageAccountsView.js`
// iterates the `users` collection and joins workspaces onto it by owner, so a
// customer with no profile row is INVISIBLE in admin — their workspace exists,
// their tier is assigned and billable, and the operator cannot see either.
// Nothing errors; the row simply is not there.
//
// `ensureUserAccount` deliberately does not write this document (see the long
// comment above `signInWithGoogle` in App.js for why provisioning ownership was
// consolidated there), so it is genuinely the client's job — which means every
// client entrance has to do it.

import { doc, getDoc } from 'firebase/firestore';
import { unguardedWrites } from './firestoreWrites';
import { db } from '../firebase';

// `users/{uid}` is NOT workspace-scoped, so it is not denied while a workspace
// is suspended and must not go through the guarded chokepoint -- a suspended
// customer still has to be able to sign in. `unguardedWrites` is the explicit,
// reviewable exemption for exactly that case (workspaceService.js takes the
// same route for its own users/ writes).
const { setDoc } = unguardedWrites;

/**
 * Seeds `users/{uid}` if it does not already exist. Idempotent, and safe to
 * call on every sign-in.
 *
 * Uses a one-shot `getDoc` rather than a listener (WR-02: a listener here leaks
 * on every sign-in) and awaits the write (WR-03: a dropped write should surface
 * rather than fail silently).
 *
 * @param {{uid: string, email?: string|null, displayName?: string|null}} user
 * @returns {Promise<boolean>} true when a document was created by this call.
 */
export async function ensureUserProfileDocument(user) {
    if (!user?.uid) return false;
    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);
    if (snapshot.exists()) return false;

    const timestamp = Date.now();
    await setDoc(userRef, {
        uid: user.uid,
        // The JSDoc types both as optional. Firestore rejects undefined, and a
        // missing field would drop the whole profile write; null is accepted.
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        createdAt: timestamp,
        updatedAt: timestamp,
    });
    return true;
}
